/**
 * useEditSystems hook
 */

// React
import { useEffect, useLayoutEffect, useState } from 'react';

// Libraries
import {
  Control,
  FieldErrors,
  useForm,
  UseFormHandleSubmit,
  UseFormTrigger,
  UseFormWatch,
  useWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useSetRecoilState } from 'recoil';

// Utils
import {
  FORMAT_DATE,
  NOTIFICATION_MESSAGES,
  StatusKey,
  VALIDATION_MESSAGES,
} from 'utils/constants';
import { defaultRadioStatusRequested, scrollToTop } from 'utils/helper/common';

// Recoil
import { notifyAtom } from 'recoil/NotifyRecoil';
import { loadingAtom } from 'recoil/LoadingRecoil';

// Router
import { DataSystemApplicationSettingCheck } from 'utils/types';

// Services
import UserService from 'services/UserService';

// Interface
interface UseEditSystemsType {
  control: Control<DataSystemApplicationSettingCheck, any>;
  modalConfirm: boolean;
  errors: FieldErrors<DataSystemApplicationSettingCheck>;
  handleSubmit: UseFormHandleSubmit<DataSystemApplicationSettingCheck, undefined>;
  handleUpdate: () => Promise<void>;
  handleUsageChange: (_value: number) => void;
  watch: UseFormWatch<DataSystemApplicationSettingCheck>;
  trigger: UseFormTrigger<DataSystemApplicationSettingCheck>;
}

// Custom validate
const { START_DATE, END_DATE } = VALIDATION_MESSAGES;

const schema = yup.object().shape({
  created_at: yup.date().nullable(),
  start_date: yup.date().when(['is_used', 'isApproved'], {
    is: (is_used: number, isApprove: boolean) => is_used === 1 && !isApprove,
    then: (schema) =>
      schema
        .required(START_DATE.REQUIRED)
        .typeError(START_DATE.INVALID)
        .min(yup.ref('created_at'), START_DATE.MIN_VALUE),
    otherwise: (schema) => schema.notRequired(),
  }),
  end_date: yup.date().when(['is_used', 'isApproved'], {
    is: (is_used: number, isApprove: boolean) => is_used === 1 && !isApprove,
    then: (schema) =>
      schema
        .required(END_DATE.REQUIRED)
        .typeError(END_DATE.INVALID)
        .min(yup.ref('start_date'), END_DATE.MIN_VALUE),
    otherwise: (schema) => schema.notRequired(),
  }),
  is_used: yup.number().nullable(),
  isApproved: yup.boolean(),
}) as any;

// Hook
export const useEditSystems = ({
  handleClose,
  defaultValue,
  handleUpdateSuccess,
}: {
  handleClose: () => void;
  defaultValue: DataSystemApplicationSettingCheck | undefined;
  handleUpdateSuccess: () => Promise<void>;
}): UseEditSystemsType => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    watch,
    trigger,
  } = useForm<DataSystemApplicationSettingCheck>({
    mode: 'onSubmit',
    resolver: yupResolver<DataSystemApplicationSettingCheck>(schema),
    reValidateMode: 'onBlur',
    defaultValues: defaultValue,
  });

  useEffect(() => {
    if (defaultValue) {
      reset(defaultValue);
    }
  }, [defaultValue, reset]);

  const [modalConfirm, setModalConfirm] = useState(false);
  const setIsLoading = useSetRecoilState(loadingAtom);
  const setNotify = useSetRecoilState(notifyAtom);

  const handleUpdate = async () => {
    const dataInput = watch();
    setIsLoading(true);
    if (dataInput.isApproved) {
      dataInput.status = StatusKey.CANCELED;
      dataInput.is_used = 0;
      dataInput.start_date = null;
      dataInput.end_date = null;
    }
    const dataUpdateUserService = {
      id: dataInput.id,
      is_used: dataInput.is_used ?? defaultRadioStatusRequested(dataInput.status),
      status: dataInput.status,
      start_date: dataInput.start_date
        ? dayjs(dataInput.start_date).format(FORMAT_DATE)?.toString()
        : undefined,
      service_id: dataInput.service_id,
      end_date: dataInput.end_date
        ? dayjs(dataInput.end_date).format(FORMAT_DATE)?.toString()
        : undefined,
    };

    try {
      await UserService.updateUserService(dataInput.user_id, [dataUpdateUserService]);
      await handleUpdateSuccess();
      reset();
      setNotify({ open: true, title: NOTIFICATION_MESSAGES.SUCCESS.UPDATE });
    } catch (error) {
      setNotify({ open: true, severity: 'error', title: NOTIFICATION_MESSAGES.ERROR.UPDATE });
      console.error(error);
    } finally {
      handleClose();
      scrollToTop();
      setIsLoading(false);
      setModalConfirm(false);
    }
  };

  const handleUsageChange = (value: number) => {
    if (value === 0) {
      clearErrors();
    }
    setValue('is_used', value);
    const startDate = watch('start_date');
    if (value && !startDate && !isApproved) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      setValue('start_date', today);
    }
  };

  const isApproved = useWatch({ control, name: 'isApproved' });
  const isUsed = useWatch({ control, name: 'is_used' });
  const startDate = useWatch({ control, name: 'start_date' });

  useLayoutEffect(() => {
    if (isApproved) {
      setValue('start_date', null);
      setValue('end_date', null);
    }
    if (!startDate && !isApproved && isUsed) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      setValue('start_date', today);
    }
  }, [isApproved]);

  return {
    trigger,
    control,
    modalConfirm,
    errors,
    handleSubmit,
    handleUpdate,
    handleUsageChange,
    watch,
  };
};
